import { z } from "zod";

/**
 * Venue onboarding form schema.
 *
 * Server-side validation lives in the Supabase migration (length checks,
 * enum-less text field for booking_system). Client validation is UX; the DB
 * constraints are the security boundary.
 */

export const COUNTRIES = [
  { value: "GB", label: "United Kingdom", currency: "GBP" as const },
  { value: "US", label: "United States", currency: "USD" as const },
  { value: "CA", label: "Canada", currency: "CAD" as const },
  { value: "AU", label: "Australia", currency: "AUD" as const },
  { value: "NZ", label: "New Zealand", currency: "NZD" as const },
] as const;

/**
 * Suggestion list for the booking-system typeahead. Users can type anything;
 * this list just powers the dropdown of known systems. Curated to basketball
 * / indoor-court friendly platforms across our four markets.
 *
 * Deliberately excludes Playtomic (padel/tennis only).
 */
export const BOOKING_SYSTEM_SUGGESTIONS = [
  "Bookteq",
  "Skedda",
  "CourtReserve",
  "RecDesk",
  "ActiveNet",
  "Perfect Mind",
  "MaxGalaxy",
  "CivicRec",
  "MINDBODY",
  "Glofox",
  "Clubworx",
  "Gymmaster",
  "ClubReady",
  "MyClubhouse",
  "SportLabs",
  "TeamSnap",
  "SimplyBook.me",
  "SuperSaaS",
  "Acuity Scheduling",
  "Hivebrite",
  "Own custom system",
] as const;

/**
 * Special value for "no booking system, use the Whistll dashboard".
 * Stored in the DB verbatim so admin queries can filter Tier 2 venues easily.
 */
export const NO_BOOKING_SYSTEM = "__none__" as const;

export const venueOnboardingSchema = z.object({
  venue_name: z.string().trim().min(2, "Venue name is required").max(120),
  country: z.enum(["GB", "US", "CA", "AU", "NZ"]),
  address_line1: z.string().trim().min(3, "Address is required").max(200),
  address_line2: z.string().trim().max(200).optional().or(z.literal("")),
  city: z.string().trim().min(1, "City is required").max(100),
  region: z.string().trim().min(1, "State / region is required").max(100),
  postcode: z.string().trim().min(3, "Postcode is required").max(20),
  contact_name: z.string().trim().min(2, "Contact name is required").max(120),
  contact_email: z.string().trim().toLowerCase().email("Valid email required"),
  contact_phone: z
    .string()
    .trim()
    .min(6, "Phone number is required")
    .max(30)
    .regex(/^[+0-9 ()-]+$/, "Phone number looks invalid"),
  uses_booking_system: z.enum(["yes", "no"], {
    errorMap: () => ({ message: "Let us know if you use a booking system" }),
  }),
  booking_system: z.string().trim().max(120).optional().or(z.literal("")),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
  accept_terms: z.literal(true, {
    errorMap: () => ({ message: "You must accept the terms to continue" }),
  }),
}).superRefine((data, ctx) => {
  if (data.uses_booking_system === "yes") {
    if (!data.booking_system || data.booking_system.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["booking_system"],
        message: "Tell us which system you use",
      });
    }
  }
});

export type VenueOnboardingFormValues = z.infer<typeof venueOnboardingSchema>;
